'use client'

import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  ReactNode,
  useEffect,
  useRef,
} from 'react'
import { useAuth } from '@/providers/auth-provider'
import { CompanyLabel, CompanyType, FundType } from '../types/companies'
import { Company } from '@/payload-types'
import { getWithErrorHandling } from '../helpers/fetch-helpers'
import { useTwoFactorBlocked } from '../hooks/useTwoFactorBlocked'

type CompaniesContextType = {
  companies: CompanyType[]
  funds: FundType[]
  labels: CompanyLabel[]
  stockIdToLabels: Map<string, CompanyLabel[]>
  refreshCompanies: () => Promise<void>
}

const CompaniesContext = createContext<CompaniesContextType | undefined>(undefined)

export const CompaniesProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth()
  // Impersonation is exempt from the limited-session gate (#3891) — see useTwoFactorBlocked.
  const twoFactorBlocked = useTwoFactorBlocked()
  const [companies, setCompanies] = useState<CompanyType[]>([])
  const [funds, setFunds] = useState<FundType[]>([])
  const [stockNumberMap, setStockNumberMap] = useState<Map<string, string>>(new Map())
  // Track in-flight fetches so we can abort them on logout / unmount.
  // Without this, requests started just before logout complete after the
  // auth cookie is cleared and come back as 401 — surfacing in the error
  // dialog on /auth/signin (see logout 401 race report).
  const detailsAbortRef = useRef<AbortController | null>(null)
  const stocksAbortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    // A 2FA-pending user holds a limited session (#3459): the server denies tenant data with
    // 403 until verification completes, so don't fire the fetch (#3566). Treat it like "no user".
    if (!user || twoFactorBlocked) {
      stocksAbortRef.current?.abort()
      stocksAbortRef.current = null
      return
    }
    const controller = new AbortController()
    stocksAbortRef.current = controller
    const fetchStockNumbers = async () => {
      try {
        const result = await getWithErrorHandling('/api/stocks/tradable', {
          errorMessage: 'שגיאה בטעינת מספרי ניירות ערך',
          notifyOnError: false,
          signal: controller.signal,
        })
        const stocks = Array.isArray(result?.stocks) ? result.stocks : []
        const map = new Map<string, string>()
        for (const stock of stocks) {
          if (stock.id && stock.stockId) {
            map.set(String(stock.id), String(stock.stockId))
          }
        }
        setStockNumberMap(map)
      } catch (_error) {
        // silently fail (including AbortError on logout/unmount)
      }
    }
    fetchStockNumbers()
    return () => {
      controller.abort()
      stocksAbortRef.current = null
    }
  }, [user, twoFactorBlocked])

  const { labels, stockIdToLabels } = useMemo(() => {
    const labelsMap = new Map<string, CompanyLabel>()
    for (const company of companies) {
      for (const lbl of company.labels || []) {
        if (lbl.labelId && !labelsMap.has(lbl.labelId)) {
          labelsMap.set(lbl.labelId, lbl)
        }
      }
    }
    const allLabels = Array.from(labelsMap.values())

    const stockMap = new Map<string, CompanyLabel[]>()
    for (const lbl of allLabels) {
      for (const s of lbl.stock || []) {
        const internalId = typeof s === 'string' ? s : String(s.stockId ?? s.id)
        if (!internalId || internalId === 'undefined') continue
        const stockNumber = stockNumberMap.get(internalId) ?? internalId
        if (!stockMap.has(stockNumber)) stockMap.set(stockNumber, [])
        const arr = stockMap.get(stockNumber)!
        if (
          !arr.some(
            (existing) =>
              existing.labelId === lbl.labelId ||
              (existing.label === lbl.label && existing.color === lbl.color),
          )
        ) {
          arr.push(lbl)
        }
      }
    }
    return { labels: allLabels, stockIdToLabels: stockMap }
  }, [companies, stockNumberMap])

  const fetchData = useCallback(async () => {
    // Abort any previous in-flight /api/companies/details fetch so a stale
    // one from before logout doesn't reject (or worse, succeed with a 401)
    // after the user is gone — that 401 used to surface in error-dialog on
    // /auth/signin.
    detailsAbortRef.current?.abort()
    const controller = new AbortController()
    detailsAbortRef.current = controller
    try {
      const result = await getWithErrorHandling('/api/companies/details', {
        errorMessage: 'שגיאה בטעינת נתוני החברות',
        // Don't pop a user-facing dialog when we get a 401 — this is the
        // logout race; the request is no longer meaningful. Genuine server
        // errors still throw and are logged by fetchWithErrorHandling.
        notifyOnError: false,
        signal: controller.signal,
      })

      const allCompanies = result.data
      const companyMap = new Map<string, Company>(allCompanies?.map((c: Company) => [c.id, c]))

      // === Create detailed companies array ===
      const detailedCompanies = (user?.companies || [])
        .map((company) => {
          const id = typeof company === 'string' ? company : company.companyId
          if (typeof id !== 'string') return null
          const fullCompany = companyMap.get(id)
          if (!fullCompany || !(fullCompany as Company)) return null
          return {
            id: fullCompany.id,
            name: fullCompany.name,
            parentCompany: fullCompany.parentCompany ?? null,
            subCompanies: fullCompany.subCompanies,
            labels: fullCompany.label?.labels,
          }
        })
        .filter(Boolean)

      // === Create detailed funds array ===
      const detailedFunds = (user?.funds || [])
        .map((company: string | { id: string }) => {
          const id = typeof company === 'string' ? company : company.id
          const fullCompany = companyMap.get(id)
          if (!fullCompany) return null
          return {
            id: fullCompany.id,
            name: fullCompany.name,
            parentCompany: fullCompany.parentCompany ?? null,
            passiveFund: fullCompany.passiveFund || false,
            canHaveSubAccounts: fullCompany.canHaveSubAccounts || false,
          }
        })
        .filter(Boolean)

      setCompanies(detailedCompanies as CompanyType[])
      setFunds(detailedFunds as FundType[])
    } catch (_error) {
      // Error already logged and notified by getWithErrorHandling
      setCompanies([])
      setFunds([])
    }
  }, [user])

  const refreshCompanies = useCallback(async () => {
    await fetchData()
  }, [fetchData])

  useEffect(() => {
    // Logout / pre-auth OR a 2FA-pending limited session (#3459): cancel any in-flight
    // /api/companies/details so it doesn't resolve with a 401 after the cookie is gone, and
    // don't start a new one — a pending session is denied tenant data with 403 (#3566).
    if (!user || twoFactorBlocked) {
      detailsAbortRef.current?.abort()
      detailsAbortRef.current = null
      setCompanies([])
      setFunds([])
      return
    }
    fetchData()
    return () => {
      detailsAbortRef.current?.abort()
      detailsAbortRef.current = null
    }
  }, [user, fetchData, twoFactorBlocked])

  return (
    <CompaniesContext.Provider
      value={{ companies, funds, labels, stockIdToLabels, refreshCompanies }}
    >
      {children}
    </CompaniesContext.Provider>
  )
}

export const useCompaniesContext = () => {
  const context = useContext(CompaniesContext)
  if (!context) {
    throw new Error('useCompaniesContext must be used within a CompaniesProvider')
  }
  return context
}
